import React, { useState, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Upload,
  FileText,
  Trash2,
  ExternalLink,
  Loader2,
  File,
  FileCheck,
  AlertCircle
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  { key: 'transcripts', label: 'Academic Transcripts', description: 'Official academic transcripts from previous institutions', icon: FileText, acceptedFormats: '.pdf,.jpg,.jpeg,.png' },
  { key: 'testScores', label: 'Test Scores', description: 'IELTS, TOEFL, GRE, or other test score reports', icon: FileCheck, acceptedFormats: '.pdf,.jpg,.jpeg,.png' },
  { key: 'sop', label: 'Statement of Purpose', description: 'Personal statement or SOP for applications', icon: File, acceptedFormats: '.pdf,.doc,.docx' },
  { key: 'recommendation', label: 'Recommendation Letters', description: 'Letters of recommendation from professors or employers', icon: FileText, acceptedFormats: '.pdf,.jpg,.jpeg,.png' },
  { key: 'resume', label: 'Resume / CV', description: 'Updated curriculum vitae or resume', icon: File, acceptedFormats: '.pdf,.doc,.docx' },
  { key: 'passport', label: 'Passport', description: 'Passport photo page scan', icon: FileText, acceptedFormats: '.pdf,.jpg,.jpeg,.png' }
];

export default function AdminStudentDocumentsTab({ studentId, documents, onUpdate }) {
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const fileInputRefs = useRef({});

  const handleFileSelect = async (documentType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(documentType);
    try {
      await adminAPI.uploadStudentDocument(studentId, file, documentType);
      toast.success('Document uploaded successfully');
      onUpdate();
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(error.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(null);
      if (fileInputRefs.current[documentType]) {
        fileInputRefs.current[documentType].value = '';
      }
    }
  };

  const handleDeleteClick = (docType) => {
    setDocumentToDelete(docType);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    setDeleting(documentToDelete);
    setDeleteDialogOpen(false);
    try {
      await adminAPI.deleteStudentDocument(studentId, documentToDelete);
      toast.success('Document deleted successfully');
      onUpdate();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error(error.response?.data?.error || 'Failed to delete document');
    } finally {
      setDeleting(null);
      setDocumentToDelete(null);
    }
  };

  const getDocumentUrl = (docType) => documents?.[docType] || null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Student Documents</h3>
          <p className="text-sm text-gray-500">View, upload and manage documents for this student</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DOCUMENT_TYPES.map((docType) => {
          const Icon = docType.icon;
          const documentUrl = getDocumentUrl(docType.key);
          const isUploading = uploading === docType.key;
          const isDeleting = deleting === docType.key;
          const hasDocument = !!documentUrl;

          return (
            <Card
              key={docType.key}
              className={`p-4 border transition-all ${
                hasDocument ? 'bg-green-50/50 border-green-200' : 'bg-stone-50/50 border-stone-200'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${hasDocument ? 'bg-green-100 text-green-600' : 'bg-stone-100 text-gray-500'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{docType.label}</h4>
                    {hasDocument && <Badge className="bg-green-100 text-green-700 text-xs">Uploaded</Badge>}
                  </div>
                  <p className="text-sm text-gray-500 mb-3">{docType.description}</p>

                  <div className="flex items-center gap-2">
                    {hasDocument ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => window.open(documentUrl, '_blank')} className="text-blue-600 hover:text-blue-700">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteClick(docType.key)} disabled={isDeleting} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1" />Delete</>}
                        </Button>
                        <input
                          type="file"
                          ref={(el) => (fileInputRefs.current[docType.key] = el)}
                          onChange={(e) => handleFileSelect(docType.key, e.target.files[0])}
                          accept={docType.acceptedFormats}
                          className="hidden"
                        />
                        <Button size="sm" variant="ghost" onClick={() => fileInputRefs.current[docType.key]?.click()} disabled={isUploading} className="text-gray-600">
                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Replace'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <input
                          type="file"
                          ref={(el) => (fileInputRefs.current[docType.key] = el)}
                          onChange={(e) => handleFileSelect(docType.key, e.target.files[0])}
                          accept={docType.acceptedFormats}
                          className="hidden"
                        />
                        <Button size="sm" onClick={() => fileInputRefs.current[docType.key]?.click()} disabled={isUploading} className="bg-purple-500 hover:bg-purple-600 text-white">
                          {isUploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-1" />Upload</>}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Delete Document
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
